/**
 * Firmware OTA Wizard reducer (GEN-05 / Phase 2 / Plan 02-05).
 *
 * Tracks the state of the 3-step Firmware Upload wizard launched from the
 * red "Upload Firmware" button in menu-bar. Each step persists user input
 * so the user can navigate back/forward without losing it.
 *
 *   step 0  — choose robot
 *   step 1  — choose firmware version
 *   step 2  — mock progress 0..100
 *
 * Actions:
 *   SET_STEP        — change current step (clamped 0..2)
 *   SELECT_ROBOT    — set selectedRobotId; resets version+progress (cascade)
 *   SELECT_VERSION  — set selectedVersion
 *   SET_PROGRESS    — set progress percent (clamped 0..100; NaN -> 0)
 *   RESET_WIZARD    — return to initial state (used on close + completion)
 *
 * Threat mitigations (per Plan 02-05 STRIDE register):
 *   T-02-05-01 (Tampering) — robotId/version typechecked, non-string -> null
 *   T-02-05-02 (Tampering) — progress clamped via Number.isFinite + min/max
 */

const SET_STEP = 'scratch-gui/firmware-wizard/SET_STEP';
const SELECT_ROBOT = 'scratch-gui/firmware-wizard/SELECT_ROBOT';
const SELECT_VERSION = 'scratch-gui/firmware-wizard/SELECT_VERSION';
const SELECT_TRANSPORT = 'scratch-gui/firmware-wizard/SELECT_TRANSPORT';
const SET_PROGRESS = 'scratch-gui/firmware-wizard/SET_PROGRESS';
const RESET_WIZARD = 'scratch-gui/firmware-wizard/RESET_WIZARD';

const MAX_STEP = 2;

// UC-05 S-3 (Wi-Fi/OTA) + S-4 (USB cable). MOCK-ONLY — both paths drive the
// same fake progress; the choice is recorded for display only.
const VALID_TRANSPORTS = ['usb', 'wifi'];

const initialState = {
    step: 0,
    selectedRobotId: null,
    selectedVersion: null,
    transport: null,
    progress: 0
};

const clamp = function (n, min, max) {
    const num = Number(n);
    if (!Number.isFinite(num)) return min;
    return Math.max(min, Math.min(max, num));
};

const reducer = function (state, action) {
    if (typeof state === 'undefined') state = initialState;
    switch (action.type) {
    case SET_STEP:
        return Object.assign({}, state, {step: clamp(action.step, 0, MAX_STEP)});
    case SELECT_ROBOT:
        return Object.assign({}, state, {
            selectedRobotId: typeof action.robotId === 'string' ? action.robotId : null,
            selectedVersion: null,
            transport: null,
            progress: 0
        });
    case SELECT_VERSION:
        return Object.assign({}, state, {
            selectedVersion: typeof action.version === 'string' ? action.version : null
        });
    case SELECT_TRANSPORT:
        return Object.assign({}, state, {
            transport: VALID_TRANSPORTS.indexOf(action.transport) === -1 ? null : action.transport
        });
    case SET_PROGRESS:
        return Object.assign({}, state, {progress: clamp(action.percent, 0, 100)});
    case RESET_WIZARD:
        return Object.assign({}, initialState);
    default:
        return state;
    }
};

const setStep = function (step) {
    return {type: SET_STEP, step: step};
};
const selectRobot = function (robotId) {
    return {type: SELECT_ROBOT, robotId: robotId};
};
const selectVersion = function (version) {
    return {type: SELECT_VERSION, version: version};
};
const selectTransport = function (transport) {
    return {type: SELECT_TRANSPORT, transport: transport};
};
const setProgress = function (percent) {
    return {type: SET_PROGRESS, percent: percent};
};
const resetWizard = function () {
    return {type: RESET_WIZARD};
};

export {
    reducer as default,
    initialState as firmwareWizardInitialState,
    setStep,
    selectRobot,
    selectVersion,
    selectTransport,
    setProgress,
    resetWizard
};
